import serializeItemResult from '../qti/serializer/itemResult/ItemResultSerializer';
import stateRestorationService from './StateRestoration';
import apiService from './api/ApiService';
import utilService from './UtilService';
import responseSendingQueue from './ResponseSendingQueue';
import storeManager from '../store/StoreManager';
import stateCacheService from '../services/StateCache';
import responseActions from '../actions/ResponseActions';
import resultActions from '../actions/ResultActions';
import assessmentActions from '../actions/AssessmentActions';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import responseReader from '../store/readHelpers/ResponseReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import sessionReader from '../store/readHelpers/SessionReader';
import tocReader from '../store/readHelpers/TocReader';
import durationTrackingService from './DurationTrackingService';
import marketplacePropertiesService from './MarketplacePropertiesService';
import questionSelectionActions from '../actions/QuestionSelectionActions';
import objectTypes from '../constants/ObjectTypes';
import { reportError } from './ErrorReporting';
import stringUtils from '../qti/utils/StringUtils';
import constants from '../qti/Constants';
import isDrawingResponseIdentifier from './DrawingUtils';

/*
 * From the QTI specification:
 *
 * initial
 * The value to use for sessions in the initial state, as described above. This value can only be used to describe sessions
 * for which the response variable numAttempts is 0. The values of the variables are set according to the rules defined in
 * the appropriate declarations (see responseDeclaration, outcomeDeclaration and templateDeclaration).
 *
 * pendingSubmission
 * The value to use when the item variables represent a snapshot of the current values during an attempt (in other words,
 * while interacting or suspended). The values of the response variables represent work in progress that has not yet been
 * submitted for response processing by the candidate. The values of the outcome variables represent the values assigned
 * during response processing at the end of the previous attempt or, in the case of the first attempt, the default values
 * given in the variable declarations.
 *
 * pendingResponseProcessing
 * The value to use when the item variables represent the values of the response variables after submission but before
 * response processing has taken place. Again, the outcomes are those assigned at the end of the previous attempt as
 * they are awaiting response processing.
 *
 * final
 * The value to use when the item variables represent the values at the end of an attempt after response processing
 * has taken place. In other words, after the outcome values have been updated to reflect the values of the response variables.
 */
export const sessionStatusValues = {
    INITIAL: 'initial',
    PENDING_SUBMISSION: 'pendingSubmission',
    PENDING_RESPONSE_SUBMISSION: 'pendingResponseProcessing',
    FINAL: 'final',
};

export const defaultItemResult = {
    identifier: null,
    datestamp: null,
    sequenceIndex: 1,
    sessionStatus: sessionStatusValues.INITIAL,
    numAttempts: 0,
    duration: 0,
    responseIdentifiers: [],
};

const itemResultCallbackRegistry = {};

const responseService = {
    defaultResettableInteractionTypes: {
        choiceInteraction: true,
        inlineChoiceInteraction: true,
        matchInteraction: true,
        hotspotInteraction: true,
        graphicGapMatchInteraction: true,
    },

    emptyResponses: [
        '',
        '<div><p></p></div>',
    ],

    /**
     * Generates an array of choice identifiers.
     * If doShuffle === true, the array is shuffled in random order.
     *
     * @param alternatives
     * @param doShuffle
     * @returns {array} array of choice alternative identifiers
     */
    generateChoiceSequence(alternatives, doShuffle) {
        const sequence = alternatives.map(item => item.identifier);

        if (doShuffle) {
            utilService.shuffleArray(sequence);
        }

        return sequence;
    },

    /**
     * Creates a responseIdentifier object based on a responseDeclarations.
     *
     * @param responseModel
     * @returns {{identifier: *, cardinality: (null|*|string), baseType: (null|*|string|string|string), userResponse: Array}}
     */
    createResponseIdentifierWithEmptyResponse(responseModel) {
        const { alternatives, baseType, cardinality, identifier, shuffle, randomise } = responseModel;
        const responseIdentifier = { identifier, cardinality, baseType, userResponse: [] };

        if (responseModel.type === 'matchInteraction') {
            let shuffledRows = alternatives[0].map(item => item.identifier);
            let shuffledColumns = alternatives[1].map(item => item.identifier);
            switch (randomise) {
            case 'rows':
                shuffledRows = this.generateChoiceSequence(alternatives[0], shuffle);
                break;
            case 'cols':
                shuffledColumns = this.generateChoiceSequence(alternatives[1], shuffle);
                break;
            default:
                shuffledRows = this.generateChoiceSequence(alternatives[0], shuffle);
                shuffledColumns = this.generateChoiceSequence(alternatives[1], shuffle);
                break;
            }
            responseIdentifier.choiceSequence = shuffledRows.concat(shuffledColumns);
        } else if (responseModel.alternatives) {
            responseIdentifier.choiceSequence = this.generateChoiceSequence(alternatives, shuffle);
        }

        return responseIdentifier;
    },

    getLocallyStoredItemResult(question, fake) {
        return new Promise((resolve) => {
            resolve(fake ? null : false);
        });
    },

    getItemResultFromServer(question, fake) {
        return new Promise((resolve) => {
            resolve(fake ? null : false);
        });
    },

    generateInitialItemResult(question, assessmentRunId) {
        if (!question.isParsed) {
            // question has not been parsed, so no item result can be generated
            return undefined;
        }

        const itemResult = JSON.parse(JSON.stringify(defaultItemResult));
        itemResult.identifier = question.contentRevisionId;
        itemResult.datestamp = utilService.getIsoDateStamp();
        if (assessmentRunId) {
            itemResult.assessmentRunId = assessmentRunId;
        }

        const locationData = tocReader.getQuestionLocation(question.contentRevisionId);

        if (locationData && locationData.overallRespondableItemsIndex !== undefined) {
            itemResult.sequenceIndex = locationData.overallRespondableItemsIndex + 1;
        } else {
            itemResult.sequenceIndex = 1;
        }

        if (question.responseModels && question.responseModels.length === 0) {
            // case of question not containing any responses. Can happen with custom interaction.
            return itemResult;
        }

        if (question.responseModels) {
            itemResult.responseIdentifiers = question.responseModels.map(
                responseModel => this.createResponseIdentifierWithEmptyResponse(responseModel),
            );

            question.responseModels.forEach((responseModel) => {
                const { extraResponseDeclarations } = responseModel;
                if (extraResponseDeclarations) {
                    extraResponseDeclarations.forEach((responseDeclaration) => {
                        const responseIdentifier = this.createResponseIdentifierWithEmptyResponse(responseDeclaration);
                        itemResult.responseIdentifiers.push(responseIdentifier);
                    });
                }
            });
        }

        return itemResult;
    },

    /**
     * Resets all interactions, and corresponding responses, that are of a resettable type to the default value.
     *
     * @param {int} contentRevisionId A question from the assessment state. Must be parsed!
     * @param {boolean} addToSendingQueue If the change should be reflected on the server
     */
    resetResponsesForQuestion(contentRevisionId, addToSendingQueue = true) {
        console.log(`resetResponsesForQuestion, contentRevisionId - ${contentRevisionId}, addToSendingQueue - ${addToSendingQueue}`);
        if (sessionReader.isContextAnyViewDeliveryType()) {
            return;
        }

        const store = storeManager.getStore();
        const stateCache = stateCacheService.getStateCacheInstance();

        // Update state
        store.dispatch(
            responseActions.resetItemResult(contentRevisionId),
        );

        // Reset feedback
        store.dispatch(
            assessmentActions.assessmentResetFeedbacksForQuestion()
        );
        // Get the updated response and save it locally
        const questionResponse = responseReader.getItemResultForQuestion(contentRevisionId);
        stateCache.setQuestionResponse(contentRevisionId, questionResponse);

        if (sessionReader.isPublicExam()) {
            return;
        }

        if (addToSendingQueue) {
            // Queue the question response for saving to the back end.
            window.console.log(`Queue response for contentRevisionId ${contentRevisionId} from resetResponsesForQuestion`);
            responseSendingQueue.getInstance().queueResponse(contentRevisionId);
        }
    },

    removeAllItemResults() {
        const store = storeManager.getStore();
        const stateCache = stateCacheService.getStateCacheInstance();

        if (stateCache) {
            stateCache.removeAllCachedTestData();
        }

        store.dispatch(
            responseActions.removeAllItemResultsInState(),
        );
    },

    /**
     * Gets an array of all the interactions for a question that are of a type that can be reset.
     *
     * @param {object} question A question from the assessment state. Must be parsed!
     * @returns {array}
     */
    getResettableInteractionIds(question) {
        const { responseModels } = question;

        // Get all interactions for the question.
        // Check if the interaction type is resettable.
        return responseModels.filter(responseModel => this.isResettableInteractionType(responseModel.type))
            .map(responseModel => responseModel.identifier);
    },

    /**
     * @param {string} type Name of an interaction type
     * @returns {boolean}
     */
    isResettableInteractionType(type) {
        return this.defaultResettableInteractionTypes[type] || false;
    },

    shouldStoreResponses() {
        return sessionReader.isContextExam()
            && !sessionReader.isPublicExam();
    },

    /**
     * Returns boolean indicating whether or not the isProcessing flag is set
     * @param responses
     * @returns {boolean}
     */
    isResponseProcessingComplete(responses) {
        const metadata = responses.metaData;
        const isProcessing = metadata.isProcessing;
        if (isProcessing) {
            return false;
        }
        return true;
    },

    /**
     * Checks if a question has any valid user responses for any of its interactions.
     * For the given interactionIdentifier, the passed newUserResponse is checked instead of the responses in the stored itemResult.
     *
     * @param {string} interactionIdentifier
     * @param {array} newUserResponse
     */
    shouldSessionStatusBeReset(interactionIdentifier, newUserResponse, itemResult, question) {
        const { responseIdentifiers } = itemResult;
        const qid = `question ${question.contentRevisionId}`;

        if (question.objectType === objectTypes.UPLOAD && newUserResponse?.length === 0) {
            console.log(`${qid}, should reset because objectType is content_question_qti2_upload and new user response is empty`);
            return true;
        }

        // Get an array of (any) valid, non empty, responses
        let isNewResponseBlank = false;
        const validResponses = responseIdentifiers.filter((item) => {
            // Skip drawing-related response identifiers
            if (isDrawingResponseIdentifier(item.identifier)) {
                return false;
            }

            const isDrawingResponse = question.responseModels?.some(model =>
                model.type === constants.interactionType.DRAWING_INTERACTION && model.responseIdentifier === item.identifier
            );
            if (isDrawingResponse) {
                return false;
            }

            let { userResponse } = item;

            if (item.identifier === interactionIdentifier) {
                userResponse = newUserResponse;
            }

            if (userResponse.length === 0) {
                return false;
            }

            const hasNonEmptyResponse = userResponse.some((responseItem) => {
                // If any of the items in userResponse are non empty, the whole response is considered non empty.
                return !this.emptyResponses.some(emptyItem => responseItem === emptyItem);
            });

            if (!hasNonEmptyResponse) {
                isNewResponseBlank = true;
            }

            return hasNonEmptyResponse;
        });

        const shouldReset = validResponses.length === 0 && itemResult.sessionStatus !== 'initial';
        if (shouldReset) {
            this.addSessionResetLogs(qid, isNewResponseBlank, responseIdentifiers, newUserResponse);
        }

        return shouldReset;
    },

    addSessionResetLogs(qid, isNewResponseBlank, responseIdentifiers, newUserResponse) {
        console.log(`${qid}, should reset because no valid responses and sessionStatus is not initial`);
        if (marketplacePropertiesService.isResetResponseButtonHidden()) {
            const extraLog = this.getStringifiedResponseIdentifiers(responseIdentifiers);
            console.log(`${qid}, user response before resetting - ${extraLog}`);

            if (isNewResponseBlank) {
                if (newUserResponse) {
                    console.log(`${qid}, new user response - ${newUserResponse.join(', ')}`);
                } else {
                    console.log(`${qid}, new user response is empty - ${newUserResponse}`);
                }
            }
        }
    },

    getStringifiedResponseIdentifiers(responseIdentifiers) {
        const extraLog = [];
        responseIdentifiers
            .filter(({ userResponse }) => userResponse.length > 0)
            .forEach(({ identifier, userResponse }) => {
                extraLog.push(`${identifier}: ${userResponse.join(', ')}`);
            });

        return extraLog.join('; ');
    },

    /**
     * @param contentRevisionId question from which the response comes.
     * @param interactionIdentifier of the interaction from which the response comes.
     * @param {array} userResponses value(s) from an interaction
     * @param {array} extraResponses value(s) from an interaction
     */
    cacheAndQueueUserResponse(contentRevisionId, interactionIdentifier, userResponses, extraResponses) {
        if (sessionReader.isContextAnyViewDeliveryType()) {
            return;
        }
        const question = assessmentReader.getQuestion(contentRevisionId);
        if (!question) {
            reportError(`handleUserResponse: No question found for contentRevisionId ${contentRevisionId}`);
            return;
        }

        const store = storeManager.getStore();
        const stateCache = stateCacheService.getStateCacheInstance();
        let itemResult = responseReader.getItemResultForQuestion(contentRevisionId);
        let extraResponsesToSet;

        const hasDrawingOfflineData = extraResponses?.some(er =>
            er.identifier?.endsWith('_drawingBase64')
            || er.identifier?.endsWith('_jsonData')
            || er.identifier?.endsWith('_pendingSync')
        );

        // Ignore empty string responses from text inputs that have not yet been typed in.
        if (userResponses?.length && !hasDrawingOfflineData) {
            const emptyResponse = this.emptyResponses.find(emptyItem => userResponses[0] === emptyItem);
            if (itemResult.sessionStatus === 'initial' && emptyResponse !== undefined) {
                return;
            }
        }

        durationTrackingService.setDurationOnResponse(contentRevisionId);
        itemResult = responseReader.getItemResultForQuestion(contentRevisionId);

        const isDrawingResponse = isDrawingResponseIdentifier(interactionIdentifier) ||
            question.responseModels?.some(model =>
                model.type === constants.interactionType.DRAWING_INTERACTION && model.responseIdentifier === interactionIdentifier
            );

        const shouldReset = this.shouldSessionStatusBeReset(interactionIdentifier, userResponses, itemResult, question);
        if (shouldReset) {
            console.log(`cacheAndQueueUserResponse, should reset response, interactionIdentifier - ${interactionIdentifier}`);
            this.resetResponsesForQuestion(contentRevisionId);
            // do not return if it is a drawing response
            if (!isDrawingResponse) {
                return;
            }
        }

        if (extraResponses) {
            extraResponsesToSet = extraResponses.map((extraResponse) => {
                const { identifier, candidateResponse } = extraResponse;
                return { identifier, response: candidateResponse };
            });
        }

        const sanitisedUserResponses = this.sanitiseUserResponse(userResponses);

        // Update state
        store.dispatch(
            responseActions.setInteractionResponse(question, contentRevisionId, interactionIdentifier, sanitisedUserResponses, extraResponsesToSet),
        );

        if (!this.shouldStoreResponses()) {
            // Do not store responses in local storage or on the server when previewing a question. Check answer handles this.
            // Same goes when previewing assessment run or when taking a public exam
            return;
        }

        // Get the updated response and save it locally
        const updatedItemResult = responseReader.getItemResultForQuestion(contentRevisionId);
        stateCache.setQuestionResponse(contentRevisionId, updatedItemResult);

        // Queue the question response for saving to the back end.
        window.console.log(`Queue response for contentRevisionId ${contentRevisionId} from handleUserResponse`);
        responseSendingQueue.getInstance().queueResponse(contentRevisionId);
    },

    handleUserResponse(contentRevisionId, interactionIdentifier, userResponses, extraResponses) {
        try {
            this.cacheAndQueueUserResponse(contentRevisionId, interactionIdentifier, userResponses, extraResponses);
        } catch (error) {
            const extraLogData = {
                contentRevisionId,
                interactionIdentifier,
                userResponses,
                extraResponses,
            };

            let baseErrorMessage = 'handleUserResponse error.';
            if (error.toString) {
                baseErrorMessage += ` ${error.toString()}.`;
            }

            reportError(baseErrorMessage, extraLogData);
            if (utilService.isIep()) {
                const iepErrorMessage = `${baseErrorMessage} ${JSON.stringify(extraLogData)}`;
                console.warn(iepErrorMessage);
            }
        }
    },

    sanitiseUserResponse(userResponses) {
        const sanitisedUserResponses = marketplacePropertiesService.shouldSanitiseNonPrintableChars() && Array.isArray(userResponses)
            ? userResponses.map(response => stringUtils.removeNonPrintableChars(response))
            : userResponses;

        return sanitisedUserResponses;
    },

    /**
     * Since duration is the time the user spent on the question, not until answered, we have to do an extra
     * check when leaving the question if the last answer was more than 5 seconds ago, another itemResult with updated
     * duration should be queued and posted to the server.
     * @param contentRevisionId
     */
    updateItemResultOnNavigation(contentRevisionId) {
        if (sessionReader.isContextAnyViewDeliveryType()
            || sessionReader.isContextViewGradingQuestionPreview()
            || sessionReader.isContextPreview()
            || sessionReader.isModeQuestion()) {
            return;
        }

        const itemResult = responseReader.getItemResultForQuestion(contentRevisionId);
        if (!itemResult) {
            return;
        }

        // IA1-5745 - always queue responses on navigation
        if (settingsReader.isCatTestType()) {
            responseSendingQueue.getInstance().queueResponse(contentRevisionId);
            return;
        }

        const secondsSinceQuestionDisplay = durationTrackingService.getSecondsSinceLastReferenceTimestamp(contentRevisionId);
        if (secondsSinceQuestionDisplay < durationTrackingService.DURATION_UPDATE_THROTTLE_LIMIT) {
            // If seconds since the question was displayed is less than the throttle limit, it should not store anything.
            return;
        }

        const lastSavedTime = Date.parse(itemResult.datestamp);
        const currentTime = Date.now();
        const secondsSinceLastSave = Math.round((currentTime - lastSavedTime) / 1000);

        if (secondsSinceLastSave > durationTrackingService.DURATION_UPDATE_THROTTLE_LIMIT) {
            // if more than DURATION_UPDATE_THROTTLE_LIMIT (to save requests) since last save,
            // send itemResult with updated duration

            const didSetDuration = durationTrackingService.setDurationOnResponse(contentRevisionId);
            if (!didSetDuration) {
                return;
            }

            const updatedItemResult = responseReader.getItemResultForQuestion(contentRevisionId);
            if (updatedItemResult) {
                stateCacheService
                    .getStateCacheInstance()
                    .setQuestionResponse(contentRevisionId, updatedItemResult);

                if (sessionReader.isPublicExam()) {
                    return;
                }

                // Queue the question response for saving to the back end.
                window.console.log(`Queue response for contentRevisionId ${contentRevisionId} from updateItemResultOnNavigation`);
                responseSendingQueue.getInstance().queueResponse(contentRevisionId);
            }
        }
    },

    persistAndQueueUpdatedItemResult(contentRevisionId, sourceLabel) {
        durationTrackingService.setDurationOnResponse(contentRevisionId);
        const updatedItemResult = responseReader.getItemResultForQuestion(contentRevisionId);
        const stateCache = stateCacheService.getStateCacheInstance();
        stateCache.setQuestionResponse(contentRevisionId, updatedItemResult);
        window.console.log(`Queue response for contentRevisionId ${contentRevisionId} from ${sourceLabel}`);
        responseSendingQueue.getInstance().queueResponse(contentRevisionId);
    },

    storeStudentHighlights(contentRevisionId, studentHighlights) {
        if (!sessionReader.isContextExam()) return;
        const store = storeManager.getStore();

        store.dispatch(
            responseActions.setStudentHighlights(contentRevisionId, studentHighlights)
        );

        // Only track duration, persist and queue in contexts where responses should be stored
        if (this.shouldStoreResponses()) {
            this.persistAndQueueUpdatedItemResult(contentRevisionId, 'storeStudentHighlights');
        }
    },

    storeQuestionAnnotations(contentRevisionId, annotations) {
        if (!sessionReader.isContextExam()) return;
        const store = storeManager.getStore();

        store.dispatch(
            responseActions.setQuestionAnnotations(contentRevisionId, annotations)
        );

        // Only track duration, persist and queue in contexts where responses should be stored
        if (this.shouldStoreResponses()) {
            this.persistAndQueueUpdatedItemResult(contentRevisionId, 'setQuestionAnnotations');
        }
    },

    fetchUserResponsesAndResetState() {
        return apiService.fetchUserResponses()
            .then((remoteUserResponses) => {
                const parsedItemResults = stateRestorationService.parseItemResultXMLsAsMap(remoteUserResponses.questions);
                const store = storeManager.getStore();
                const keys = Object.keys(parsedItemResults);

                if (keys.length) {
                    // Set the result state.
                    const results = responseReader.getResultsFromResponses(
                        remoteUserResponses.questions,
                        { response: parsedItemResults }
                    );
                    storeManager.getStore()
                        .dispatch(
                            resultActions.setResultState({
                                questions: results,
                                metaData: remoteUserResponses.metaData,
                            })
                        );
                }

                store.dispatch(
                    responseActions.setResponseState(parsedItemResults),
                );
            });
    },

    fetchUserResponseForOneQuestion(contentRevisionId) {
        return apiService.fetchUserResponseForOneQuestion(contentRevisionId)
            .then((fetchedUserResponse) => {
                const itemResults = stateRestorationService.parseItemResultXMLsAsMap(fetchedUserResponse.questions);
                const store = storeManager.getStore();
                const question = assessmentReader.getQuestion(contentRevisionId);
                store.dispatch(
                    responseActions.setItemResult(question, contentRevisionId, itemResults[contentRevisionId]),
                );

                const questionResults = responseReader.getResultsFromResponses(fetchedUserResponse.questions);
                store.dispatch(resultActions.setResultState({
                    questions: questionResults,
                    metaData: {
                        containsManualScores: fetchedUserResponse.questions[contentRevisionId].manualScore.length > 0,
                        isGraded: true,
                    },
                }));

                return fetchedUserResponse;
            });
    },

    handleContentBlockSelection(questionPairs, currQuestionId) {
        if (!questionPairs || !currQuestionId) {
            return Promise.reject(new Error('handleContentBlockSelection: No questions provided'));
        }

        const selectedQuestion = questionPairs.find(question => question?.contentRevisionId === currQuestionId);
        const unselectedQuestions = questionPairs.filter(question => question?.contentRevisionId !== currQuestionId);

        if (!selectedQuestion || unselectedQuestions?.length === 0) {
            return Promise.reject(new Error('handleContentBlockSelection: Could not find questions in content blocks'));
        }

        storeManager.getStore().dispatch(
            questionSelectionActions.setCSCBSelection({
                [selectedQuestion.contentRevisionId]: constants.selectionType.YES,
                [unselectedQuestions[0]?.contentRevisionId]: constants.selectionType.NO,
            })
        );

        responseSendingQueue.getInstance().queueResponses([selectedQuestion.contentRevisionId, unselectedQuestions[0]?.contentRevisionId]);
        return true;
    },

    sendUserResponseForPreview(contentRevisionId) {
        const questionResponse = responseReader.getItemResultForQuestion(contentRevisionId);
        if (!questionResponse) {
            Promise.reject();
        }
        const itemResultXML = serializeItemResult(questionResponse, false);

        return apiService.storeUserResponse(itemResultXML, contentRevisionId);
    },

    fetchFeedbackForOneQuestion(contentRevisionId) {
        const shouldShowFeedback =
            sessionReader.isModeQuestion() ||
            sessionReader.isModeQuestionSet() ||
            sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false);

        const shouldSkipFeedbackFetch =
            !(shouldShowFeedback && settingsReader.isFeedbackEnabledInMarketplace());

        if (shouldSkipFeedbackFetch) {
            return Promise.resolve();
        }

        return apiService.fetchFeedbackForQuestion(contentRevisionId)
            .then((feedbacks) => {
                storeManager.dispatch(
                    assessmentActions.assessmentFeedbackForQuestionsLoaded(feedbacks)
                );
            });
    },

    saveResponseAndFetchResponse(contentRevisionId) {
        return this.sendUserResponseForPreview(contentRevisionId)
            .then(() => this.fetchUserResponseForOneQuestion(contentRevisionId))
            .then(() => this.fetchFeedbackForOneQuestion(contentRevisionId));
    },

    /**
     * Useful for questions with auto-save (i.e. Essay, Programming, Text Area).
     * The callback that is updating the item result should be registered here,
     * and called when the response must be updated on-demand via updateItemResult().
     * Use case: at 'Check answer' action in Preview.
     * @param contentRevisionId
     * @param callback
     */
    subscribeToItemResultUpdates(contentRevisionId, callback) {
        itemResultCallbackRegistry[contentRevisionId] = callback;
    },

    /**
     * Calls the callback registered for a certain question in order to update the item result.
     * @param contentRevisionId
     */
    updateItemResult(contentRevisionId) {
        const callback = itemResultCallbackRegistry[contentRevisionId];
        if (typeof callback === 'function') {
            callback(true);
        }
    },

    /**
     * Get longest alternative
     * @param alternatives
     */
    getLongestAlternative(alternatives) {
        return [...alternatives].sort((a, b) => b.content.length - a.content.length)[0];
    },

    /**
     * Get all alternatives related to model
     * @param model
     */
    getAlternatives(model) {
        const alternatives = Array.from(model.alternatives);

        return alternatives.map((alternative) => {
            const { content } = alternative;

            if (content.indexOf('&lt;') !== -1) {
                return Object.assign({}, alternative, { content: content.replace(/&lt;/gi, '<') });
            }
            return alternative;
        });
    },

    /**
     * Get value from user response
     * @param userResponse
     */
    getValue(userResponse) {
        return userResponse.join('');
    },

    isQuestionAnswerCommittingUnattempted(questionResponse) {
        return !questionResponse || !questionResponse.commitAnswerTimestamp;
    },

    isQuestionCommitAnswerConfirmNeeded(questionResponse) {
        return !!questionResponse && !questionResponse.commitAnswerTimestamp
            && questionResponse.commitAnswerStatus === responseActions.commitTimestampStatus.CONFIRM;
    },

    isQuestionCommitAnswerPending(questionResponse) {
        return !!questionResponse?.commitAnswerTimestamp && questionResponse.commitAnswerStatus === responseActions.commitTimestampStatus.PENDING;
    },

    isQuestionCommitAnswerReadyToBeStored(questionResponse) {
        return sessionReader.isCommittingAnswerSupported()
            && this.isQuestionCommitAnswerPending(questionResponse);
    },

    isQuestionCommitAnswerAlreadyStored(questionResponse) {
        return sessionReader.isCommittingAnswerSupported()
            && this.isQuestionCommitAnswerStored(questionResponse);
    },

    isQuestionCommitAnswerStored(questionResponse) {
        return !!questionResponse?.commitAnswerTimestamp && questionResponse.commitAnswerStatus === responseActions.commitTimestampStatus.STORED;
    },

    setQuestionCommitAnswerPendingStatus(questionId) {
        storeManager.dispatch(responseActions.setPendingQuestionCommitTimestamp(questionId));

        if (responseService.shouldStoreResponses()) {
            const updatedItemResult = responseReader.getItemResultForQuestion(questionId);
            stateCacheService.getStateCacheInstance().setQuestionResponse(questionId, updatedItemResult);
            responseSendingQueue.getInstance().queueResponse(questionId);
        }
    },

    isOneWayNavigationDisallowedOfflineMode() {
        return !utilService.isBrowserStatusOnline() && settingsReader.isOfflineOneWayNavigationDisallowed();
    },

    isReactMathKeyboardForSymbolicMathEnabled(baseType, interactionType) {
        const {
            interactionType: {
                SYMBOLIC_MATH,
            },
            baseTypes: {
                STRING,
            },
        } = constants;

        return baseType === STRING &&
            interactionType === SYMBOLIC_MATH &&
            marketplacePropertiesService.isReactMathKeyboardForSymbolicMathEnabled();
    },
};

export default responseService;
